'use client'

import { useMemo } from 'react'

import useQuickSearchData from '~/components/QuickSearch/hooks/useQuickSearchData'
import type { SearchCategory } from '~/components/QuickSearch/types'
import { useGateValue } from '~/configs/statsig'
import {
  type AgreementResponse,
  type ProductResponse,
  useGetAgreementsQuery,
  useListProductsQuery
} from '~/data/api/client/billing'
import { skipWhen } from '~/data/api/hooks/queryOptions'
import { useManagedBatteries, useManagedGenerators } from '~/data/api/manager'
import { isSharedOrg } from '~/data/types/organization'
import { useCurrentOrgId } from '~/store/slices/currentOrg'
import { normalizeStr } from '~/views/overviews/hooks'

import {
  type FocusedContextRef,
  buildAgreementContextRef,
  buildBatteryContextRef,
  buildChargerContextRef,
  buildGeneratorContextRef,
  buildOrgContextRef,
  buildProductContextRef,
  buildSiteContextRef,
  buildSynopLinkContextRef,
  buildUserContextRef,
  buildVehicleContextRef
} from './contextRef'

/** Entity categories available for @mentions in the assistant (broader than Quick Search). */
export type AssistantMentionCategory = SearchCategory | 'battery' | 'generator' | 'product' | 'agreement'

export type UseAssistantMentionsOptions = {
  /**
   * Whether the picker can show at all (the bar is open and writable). Billing entities are not part of
   * the Quick Search cache, so they are fetched only while this is true, the user has typed `@`, and
   * the `billing-composer` gate is on.
   */
  enabled?: boolean
}

/** One page is plenty for a typeahead; the billing lists are small per organization. */
const BILLING_PAGE_SIZE = 100

export type AssistantMentionCandidate = {
  category: AssistantMentionCategory
  id: string
  label: string
  subLabel?: string
  contextRef: FocusedContextRef
}

const MAX_MENTION_RESULTS = 8

/**
 * Client-side @mention search for the assistant. Reuses the same in-memory
 * entity data the Command+K palette loads (chargers, sites, vehicles, users,
 * orgs, synoplinks) via {@link useQuickSearchData}, plus batteries and generators
 * from the org data manager cache.
 */
export default function useAssistantMentions(
  query: string | null,
  { enabled = true }: UseAssistantMentionsOptions = {}
): AssistantMentionCandidate[] {
  const orgId = useCurrentOrgId()
  const billingComposerEnabled = useGateValue('billing-composer')
  const { siteItems, chargerItems, vehicleItems, userItems, organizations, synopLinkItems } = useQuickSearchData()
  const { batteries } = useManagedBatteries()
  const { generators } = useManagedGenerators()

  // `query` is null until the draft contains an `@`; the lists stay unfetched until then. An org
  // without billing gets an error here, which simply leaves both categories empty (no toast).
  // Organization id is part of the cache key so a tenant switch cannot reuse another org's page.
  const skipBilling = !enabled || query === null || !billingComposerEnabled || !orgId
  const { data: productPage } = useListProductsQuery(
    { size: BILLING_PAGE_SIZE, organizationId: orgId },
    skipWhen(skipBilling)
  )
  const { data: agreementPage } = useGetAgreementsQuery(
    { size: BILLING_PAGE_SIZE, includeArchived: false, payeeId: orgId },
    skipWhen(skipBilling)
  )
  const products = useMemo(() => activeFirst(productPage?.data ?? []), [productPage])
  const agreements = useMemo(() => activeFirst(agreementPage?.data ?? []), [agreementPage])

  return useMemo(() => {
    if (!query) return []
    const normalized = normalizeStr(query)
    if (!normalized) return []

    const candidates: AssistantMentionCandidate[] = []

    for (const charger of chargerItems) {
      if (candidates.length >= MAX_MENTION_RESULTS) break
      const nameMatches = normalizeStr(charger.chargerName)?.includes(normalized)
      const idMatches = normalizeStr(charger.chargerId)?.includes(normalized)
      const makeMatches = normalizeStr(charger.manufacturerName)?.includes(normalized)
      const modelMatches = normalizeStr(charger.makeModelName)?.includes(normalized)
      if (nameMatches || idMatches || makeMatches || modelMatches) {
        const contextRef = buildChargerContextRef(charger)
        candidates.push({
          category: 'charger',
          id: charger.chargerId,
          label: contextRef.label ?? charger.chargerId,
          subLabel: charger.depotName ?? undefined,
          contextRef
        })
      }
    }

    for (const site of siteItems) {
      if (candidates.length >= MAX_MENTION_RESULTS) break
      if (normalizeStr(site?.depotNm)?.includes(normalized)) {
        const contextRef = buildSiteContextRef(site)
        candidates.push({
          category: 'site',
          id: site.depotId,
          label: contextRef.label ?? site.depotId,
          contextRef
        })
      }
    }

    for (const vehicle of vehicleItems) {
      if (candidates.length >= MAX_MENTION_RESULTS) break
      const nameMatches = normalizeStr(vehicle?.vehicleNm)?.includes(normalized)
      const displayNameMatches = normalizeStr(vehicle?.vehicleDisplayNm)?.includes(normalized)
      const vinMatches = normalizeStr(vehicle?.vin)?.includes(normalized)
      const makeMatches = normalizeStr(vehicle?.make)?.includes(normalized)
      const modelMatches = normalizeStr(vehicle?.model)?.includes(normalized)
      if ((nameMatches || displayNameMatches || vinMatches || makeMatches || modelMatches) && vehicle.id) {
        const contextRef = buildVehicleContextRef(vehicle)
        candidates.push({
          category: 'vehicle',
          id: vehicle.id,
          label: contextRef.label ?? vehicle.id,
          contextRef
        })
      }
    }

    for (const battery of batteries) {
      if (candidates.length >= MAX_MENTION_RESULTS) break
      const nameMatches = normalizeStr(battery.name)?.includes(normalized)
      const idMatches = normalizeStr(battery.id)?.includes(normalized)
      const makeMatches = normalizeStr(battery.manufacturer)?.includes(normalized)
      const modelMatches = normalizeStr(battery.modelNumber)?.includes(normalized)
      if (nameMatches || idMatches || makeMatches || modelMatches) {
        const contextRef = buildBatteryContextRef(battery)
        candidates.push({
          category: 'battery',
          id: battery.id,
          label: contextRef.label ?? battery.id,
          contextRef
        })
      }
    }

    for (const generator of generators) {
      if (candidates.length >= MAX_MENTION_RESULTS) break
      const nameMatches = normalizeStr(generator.name)?.includes(normalized)
      const idMatches = normalizeStr(generator.id)?.includes(normalized)
      const makeMatches = normalizeStr(generator.manufacturer)?.includes(normalized)
      const modelMatches = normalizeStr(generator.modelNumber)?.includes(normalized)
      if (nameMatches || idMatches || makeMatches || modelMatches) {
        const contextRef = buildGeneratorContextRef(generator)
        candidates.push({
          category: 'generator',
          id: generator.id,
          label: contextRef.label ?? generator.id,
          contextRef
        })
      }
    }

    for (const product of products) {
      if (candidates.length >= MAX_MENTION_RESULTS) break
      const nameMatches = normalizeStr(product.name)?.includes(normalized)
      const idMatches = normalizeStr(product.id)?.includes(normalized)
      if (nameMatches || idMatches) {
        const contextRef = buildProductContextRef(product)
        candidates.push({
          category: 'product',
          id: product.id,
          label: contextRef.label ?? product.id,
          contextRef
        })
      }
    }

    for (const agreement of agreements) {
      if (candidates.length >= MAX_MENTION_RESULTS) break
      if (!agreement.id) continue
      const nameMatches = normalizeStr(agreement.name)?.includes(normalized)
      const idMatches = normalizeStr(agreement.id)?.includes(normalized)
      const partnerMatches = normalizeStr(agreement.payorOrganizationName)?.includes(normalized)
      if (nameMatches || idMatches || partnerMatches) {
        const contextRef = buildAgreementContextRef({ id: agreement.id, name: agreement.name })
        candidates.push({
          category: 'agreement',
          id: agreement.id,
          label: contextRef.label ?? agreement.id,
          subLabel: agreement.payorOrganizationName ?? undefined,
          contextRef
        })
      }
    }

    for (const user of userItems ?? []) {
      if (candidates.length >= MAX_MENTION_RESULTS) break
      if (user.id && normalizeStr(user?.name)?.includes(normalized)) {
        const contextRef = buildUserContextRef(user)
        candidates.push({
          category: 'user',
          id: user.id,
          label: contextRef.label ?? user.id,
          contextRef
        })
      }
    }

    for (const org of organizations) {
      if (candidates.length >= MAX_MENTION_RESULTS) break
      if (!isSharedOrg(org) && org.id && normalizeStr(org?.organizationNm)?.includes(normalized)) {
        const contextRef = buildOrgContextRef({ id: org.id, organizationNm: org.organizationNm })
        candidates.push({
          category: 'org',
          id: org.id,
          label: contextRef.label ?? org.id,
          contextRef
        })
      }
    }

    for (const synopLink of synopLinkItems) {
      if (candidates.length >= MAX_MENTION_RESULTS) break
      const nickNameMatches = normalizeStr(synopLink.nickName)?.includes(normalized)
      const thingNameMatches = normalizeStr(synopLink.thingName)?.includes(normalized)
      const serialNumberMatches = normalizeStr(synopLink.serialNumber ?? undefined)?.includes(normalized)
      if (nickNameMatches || thingNameMatches || serialNumberMatches) {
        const contextRef = buildSynopLinkContextRef(synopLink)
        candidates.push({
          category: 'synoplink',
          id: synopLink.id,
          label: contextRef.label ?? synopLink.id,
          contextRef
        })
      }
    }

    return candidates
  }, [
    query,
    agreements,
    batteries,
    chargerItems,
    generators,
    organizations,
    products,
    siteItems,
    synopLinkItems,
    userItems,
    vehicleItems
  ])
}

/** Live entities first: an archived product is rarely what the user means by its name. */
function activeFirst<T extends ProductResponse | AgreementResponse>(items: T[]): T[] {
  const rank = (item: T) => (item.status === 'ACTIVE' ? 0 : item.status === 'ARCHIVED' ? 2 : 1)
  return [...items].sort((a, b) => rank(a) - rank(b))
}
